import classNames from "classnames";
import { useParams } from "react-router";

import { useTranslate } from "../../../../i18n/useTranslationModules";
import { ActionEvent, ActionTargetType, ActionType, CallToAction, CallToActionType, PageModuleType, Sponsor } from "../../../../types/working-model";
import { useTracking } from "../../../../utils/tracking";
import { parseUrl } from "../../../../utils/utils";
import { TypographyItem } from "../../../general-ui/typography-item/typography-item";
import useTranslationRoute from "../../hooks/use-translation-route";
import { ParamsProps } from "../../live-event";
import { stylingOverrideReturns } from "../../utils";

interface SponsorCardProps {
	sponsor: Sponsor;
	stylingOverrides: stylingOverrideReturns;
	template: string;
	handleScroll?: (moduleId?: number) => void;
	isLandingPage?: boolean;
}

const SponsorCard: React.FC<SponsorCardProps> = ({ sponsor, stylingOverrides, handleScroll }) => {
	const { language }: ParamsProps = useParams();

	const {
		moduleTitleOverrides,
		moduleDescriptionOverrides,
		customHeadingOverrides
	} = stylingOverrides;

	const { trackEvent } = useTracking();
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(translationRoute);
	const id = sponsor.module_id;

	return (
		<li className={classNames("sponsor-card")}>
			<div className="sponsor-card-container">
				<div className={classNames("sponsor-card-image")}>
					<img src={sponsor.image} alt="" aria-hidden="true" />
				</div>
				<TypographyItem
					className={classNames('sponsor-card-title stable', moduleTitleOverrides)}
					tagName="h3"
				>
					{t(`sponsor.${id}.name`, sponsor.name)}
				</TypographyItem>
				<TypographyItem className={classNames('evt-body-text-2 sponsor-card-description', moduleDescriptionOverrides)}>
					{t(`sponsor.${id}.description`, sponsor.description[language] as string || sponsor.description.base || "")}
				</TypographyItem>
				<SponsorCallToAction
					call_to_action={sponsor.call_to_action}
					callToActionTitle={t(`sponsor.${id}.callToAction`, '')}
					handleScroll={moduleId => {
						handleScroll?.(moduleId);
					}}
					className={classNames("sponsor-card-call-to-action", customHeadingOverrides)}
					language={language}
					cta_isOn={sponsor.enable_cta}
					onClick={() => {
						trackEvent({
							action: ActionEvent.Click,
							target_type: ActionTargetType.Sponsor,
							target_id: sponsor.sponsor,
							action_type: ActionType.Active
						});
					}}
				/>
			</div>
		</li>
	);
};

export default SponsorCard;

interface Props {
	call_to_action: CallToAction;
	className?: string;
	handleScroll: (moduleId?: number, moduleType?: PageModuleType) => void;
	language: string;
	cta_isOn: boolean;
	onClick?: () => void;
	callToActionTitle?: string;
	isLandingPage?: boolean;
}

export const SponsorCallToAction: React.FC<Props> = ({ call_to_action, className, handleScroll, language, cta_isOn, onClick, callToActionTitle }: Props) => {
	if (cta_isOn) {
		let link;

		const text = callToActionTitle || (call_to_action.button_name[language] ?? call_to_action.button_name.base);
		switch (call_to_action.type) {
			case CallToActionType.page:
				// TODO: add link to page once built and routes have been determined
				link = <a
					className={className}
					href={call_to_action.content.page}
					target="_blank" rel="noreferrer"
					onClick={() => onClick ? onClick() : null}
				>
					{text}
				</a>;
				break;
			case CallToActionType.section:
				link = (
					<a
						className={className}
						onClick={() => {
							handleScroll(call_to_action?.content?.section_module_id || 0, call_to_action.content?.section || 0);
							onClick ? onClick() : null;
						}
						}>
						{text}
					</a>
				);
				break;
			case CallToActionType.url:
				link = call_to_action.content.url ?
					<a
						className={className}
						href={parseUrl(call_to_action.content.url)}
						target="_blank" rel="noreferrer"
						onClick={() => onClick ? onClick() : null}
					>
						{text}
					</a>
					:
					null;
		}
		return <>{link}</>;
	} else return <></>;
};


